import { Breakpoint, Typography, TypographyElement, TypographyVariant } from '@elearning-platform/ui';
import React from 'react';
import { useMediaQuery } from 'react-responsive';

import { DualContentSlideDetails, DualContentSlideList } from '../../interfaces';

import {
  Detail,
  DetailOperator,
  DetailsWrapper,
  Image,
  List,
  ListItem,
  ListWrapper,
  Part,
  PartsWrapper,
  Strip,
  StripsWrapper,
  StripText,
  StyledMarkdown,
  StyledSideNote,
  TextContainer,
  Wrapper,
} from './dualContentSlide.styled';
import { DualContentSlideProps } from './dualContentSlide.types';

export const DualContentSlide: React.FC<DualContentSlideProps> = (props: DualContentSlideProps): JSX.Element => {
  const { baseUrl, data, partsCount, activePart }: DualContentSlideProps = props;
  const isSmallDesktop: boolean = useMediaQuery({
    query: Breakpoint.SmallDesktop,
  });

  return (
    <Wrapper $reversed={!!data.reversed} $withImage={!!data.image}>
      {!!data.additionalImage && (
        <Image src={baseUrl + data.additionalImage} />
      )}
      {!!data.sideNoteData && (
        <StyledSideNote
          header={data.sideNoteData.header}
          image={data.sideNoteData.image ? baseUrl + data.sideNoteData.image : undefined}
          list={data.sideNoteData.list}
          subheader={data.sideNoteData.subheader}
          supportingText={data.sideNoteData.supportingText}
          text={data.sideNoteData.text}
          withCenteredContent={data.sideNoteData.withCenteredContent}
          withSideImage={data.sideNoteData.withSideImage}
        />
      )}
      {!!data.secondSideNoteData && (
        <StyledSideNote
          header={data.secondSideNoteData.header}
          list={data.secondSideNoteData.list}
          subheader={data.secondSideNoteData.subheader}
          supportingText={data.secondSideNoteData.supportingText}
          text={data.secondSideNoteData.text}
          withCenteredContent={data.secondSideNoteData.withCenteredContent}
          withSideImage={data.secondSideNoteData.withSideImage}
        />
      )}
      {(!data.image && !!data.stripItems) && (
        <StripsWrapper>
          {data.stripItems.map((strip: string): JSX.Element => (
            <Strip key={strip}>
              <Typography tag={TypographyElement.div} variant={TypographyVariant.TextSM}>
                <StripText text={strip} />
              </Typography>
            </Strip>
          ))}
        </StripsWrapper>
      )}
      {!!(!data.additionalImage && !data.secondSideNoteData && !!(data.text || data.lists || data.supportingText)) && (
        <TextContainer>
          {!!data.withParts && (
            <PartsWrapper>
              {[...Array(partsCount)].map((_: undefined, index: number): JSX.Element => (
                // eslint-disable-next-line react/no-array-index-key
                <Part $isActive={activePart === index} key={`part_${index}`} />
              ))}
            </PartsWrapper>
          )}
          {!!data.largeHeader && (
            <Typography tag={TypographyElement.div} variant={TypographyVariant.DisplaySM}>
              <StyledMarkdown text={data.largeHeader} />
            </Typography>
          )}
          {!!data.header && (
            <Typography tag={TypographyElement.div} variant={TypographyVariant.TextLG}>
              <StyledMarkdown text={data.header} />
            </Typography>
          )}
          {!!data.text && (
            <Typography tag={TypographyElement.div} variant={TypographyVariant.TextMD}>
              <StyledMarkdown text={data.text} />
            </Typography>
          )}
          {!!data.details && (
            data.details.map((detail: DualContentSlideDetails, index: number): JSX.Element => (
            // eslint-disable-next-line react/no-array-index-key
              <DetailsWrapper key={`detail_${index}`}>
                {detail.items.map((item: string, itemIndex: number): JSX.Element => (
                  <React.Fragment key={item}>
                    <Detail>
                      <Typography tag={TypographyElement.div} variant={TypographyVariant.TextSM}>
                        <StyledMarkdown text={item} />
                      </Typography>
                    </Detail>
                    {(!!detail.operator && itemIndex < detail.items.length - 1) && (
                      <DetailOperator variant={TypographyVariant.TextSM}>
                        {detail.operator}
                      </DetailOperator>
                    )}
                  </React.Fragment>
                ))}
              </DetailsWrapper>
            ))
          )}
          {data.lists?.map((list: DualContentSlideList, index: number): JSX.Element => (
            // eslint-disable-next-line react/no-array-index-key
            <ListWrapper key={`list_${index}`}>
              {!!list.header && (
                <Typography tag={TypographyElement.div} variant={TypographyVariant.TextLG}>
                  <StyledMarkdown text={list.header} />
                </Typography>
              )}
              <List>
                {list.items.map((listItem: string): JSX.Element => (
                  <ListItem key={listItem}>
                    <Typography tag={TypographyElement.div} variant={TypographyVariant.TextSM}>
                      <StyledMarkdown text={listItem} />
                    </Typography>
                  </ListItem>
                ))}
              </List>
              {!!list.supportingText && (
                <Typography tag={TypographyElement.div} variant={TypographyVariant.TextSM}>
                  <StyledMarkdown text={list.supportingText} />
                </Typography>
              )}
            </ListWrapper>
          ))}
          {(!!data.stripItems && !!data.stripsWithinText) && (
            <StripsWrapper>
              {data.stripItems.map((strip: string): JSX.Element => (
                <Strip key={strip}>
                  <Typography tag={TypographyElement.div} variant={TypographyVariant.TextSM}>
                    <StripText text={strip} />
                  </Typography>
                </Strip>
              ))}
            </StripsWrapper>
          )}
          {!!data.supportingText && (
            <Typography tag={TypographyElement.div} variant={TypographyVariant.TextSM}>
              <StyledMarkdown text={data.supportingText} />
            </Typography>
          )}
        </TextContainer>
      )}
      {!!data.image && (
        <Image
          src={(!isSmallDesktop && data.mobileImage)
            ? (baseUrl + data.mobileImage)
            : (baseUrl + data.image)}
        />
      )}
    </Wrapper>
  );
};
